import {
  doc,
  getDoc,
  updateDoc,
  Timestamp,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db } from './firebase';
import { functions } from './firebase';
import {
  TeacherSubscription,
  SubscriptionPlanId,
  BillingCycle,
  PLAN_ENTITLEMENTS,
  normalizeTeacherSubscription,
} from '../types/subscription';
import { classesService, studentsService } from './firestoreService';

// نتيجة التحقق من الحد
export interface LimitCheckResult {
  allowed: boolean;
  currentCount: number;
  limit: number;
  remaining: number;
  upgradeRequired: SubscriptionPlanId | null;
  messageAr: string;
  messageEn: string;
}

// إحصائيات الاستخدام
export interface UsageStats {
  students: { current: number; limit: number; percentage: number };
  classes: { current: number; limit: number; percentage: number };
  employees: { current: number; limit: number; percentage: number };
  sessions: { current: number; limit: number; percentage: number };
}

class SubscriptionService {
  private collection = 'subscriptions';

  async getSubscription(teacherId: string): Promise<TeacherSubscription | null> {
    try {
      const docRef = doc(db, this.collection, teacherId);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        return normalizeTeacherSubscription(snap.data() as any);
      }
      return null;
    } catch (error) {
      console.error('Error fetching subscription:', error);
      return null;
    }
  }

  /** Returns the server-owned subscription or starts the 14-day Pro trial. */
  async getOrCreateSubscription(teacherId: string): Promise<TeacherSubscription> {
    const existing = await this.getSubscription(teacherId);
    if (existing) return existing;
    const callable = httpsCallable<void, TeacherSubscription>(
      functions,
      'initializeTrialSubscription'
    );
    const result = await callable();
    return normalizeTeacherSubscription(result.data as any);
  }

  /** Paid plan changes are activated only after a verified payment capture. */
  async changePlan(
    _teacherId: string,
    _planId: SubscriptionPlanId,
    _billingCycle: BillingCycle
  ): Promise<void> {
    throw new Error('Paid subscription changes must be completed through a verified checkout.');
  }

  async cancelSubscription(_teacherId: string): Promise<void> {
    const callable = httpsCallable<void, { success: boolean }>(
      functions,
      'cancelTeacherSubscription'
    );
    await callable();
  }

  async incrementSessionCount(teacherId: string): Promise<void> {
    try {
      const docRef = doc(db, this.collection, teacherId);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const current = (snap.data() as TeacherSubscription).usageThisMonth?.sessions || 0;
        await updateDoc(docRef, {
          'usageThisMonth.sessions': current + 1,
          updatedAt: Timestamp.now(),
        });
      }
    } catch (error) {
      console.error('Error incrementing session count:', error);
    }
  }

  /** Check if a teacher is within their plan limits */
  checkLimit(
    sub: TeacherSubscription,
    check: 'students' | 'classes' | 'sessions' | 'employees',
    currentCount: number
  ): { allowed: boolean; limit: number; upgradeRequired: SubscriptionPlanId | null } {
    const plan = PLAN_ENTITLEMENTS[sub.planId];
    let limit = -1;

    if (check === 'students') limit = plan.limits.maxStudents;
    if (check === 'classes') limit = plan.limits.maxClasses;
    if (check === 'sessions') limit = plan.limits.maxSessionsPerMonth;
    if (check === 'employees') limit = plan.limits.maxEmployees;

    if (limit === -1) return { allowed: true, limit: -1, upgradeRequired: null };

    const allowed = currentCount < limit;
    const upgradeRequired = allowed ? null : this.nextPlan(sub.planId);

    return { allowed, limit, upgradeRequired };
  }

  // ======================== دوال التحقق من الحدود ========================

  /** التحقق من إمكانية إضافة طالب جديد */
  async canAddStudent(teacherId: string): Promise<LimitCheckResult> {
    const sub = await this.getOrCreateSubscription(teacherId);
    const plan = PLAN_ENTITLEMENTS[sub.planId];
    const limit = plan.limits.maxStudents;

    // جلب عدد الطلاب الحالي
    const currentCount = (await studentsService.getByTeacherId(teacherId))
      .length;

    if (limit === -1) {
      return {
        allowed: true,
        currentCount,
        limit: -1,
        remaining: -1,
        upgradeRequired: null,
        messageAr: 'طلاب غير محدودين',
        messageEn: 'Unlimited students',
      };
    }

    const allowed = currentCount < limit;
    const remaining = Math.max(0, limit - currentCount);

    return {
      allowed,
      currentCount,
      limit,
      remaining,
      upgradeRequired: allowed ? null : this.nextPlan(sub.planId),
      messageAr: allowed
        ? `يمكنك إضافة ${remaining} طالب إضافي`
        : `وصلت للحد الأقصى (${limit} طالب). قم بالترقية لإضافة المزيد.`,
      messageEn: allowed
        ? `You can add ${remaining} more students`
        : `Reached limit (${limit} students). Upgrade to add more.`,
    };
  }

  /** التحقق من إمكانية إضافة فصل جديد */
  async canAddClass(teacherId: string): Promise<LimitCheckResult> {
    const sub = await this.getOrCreateSubscription(teacherId);
    const plan = PLAN_ENTITLEMENTS[sub.planId];
    const limit = plan.limits.maxClasses;

    // جلب عدد الفصول الحالي
    const currentCount = (await classesService.getByTeacherId(teacherId))
      .length;

    if (limit === -1) {
      return {
        allowed: true,
        currentCount,
        limit: -1,
        remaining: -1,
        upgradeRequired: null,
        messageAr: 'فصول غير محدودة',
        messageEn: 'Unlimited classes',
      };
    }

    const allowed = currentCount < limit;
    const remaining = Math.max(0, limit - currentCount);

    return {
      allowed,
      currentCount,
      limit,
      remaining,
      upgradeRequired: allowed ? null : this.nextPlan(sub.planId),
      messageAr: allowed
        ? `يمكنك إضافة ${remaining} فصل إضافي`
        : `وصلت للحد الأقصى (${limit} فصل). قم بالترقية لإضافة المزيد.`,
      messageEn: allowed
        ? `You can add ${remaining} more classes`
        : `Reached limit (${limit} classes). Upgrade to add more.`,
    };
  }

  /** التحقق من إمكانية إضافة موظف جديد */
  async canAddEmployee(teacherId: string): Promise<LimitCheckResult> {
    const sub = await this.getOrCreateSubscription(teacherId);
    const plan = PLAN_ENTITLEMENTS[sub.planId];
    
    // التحقق من صلاحية إدارة الموظفين أولاً
    if (!plan.limits.canManageEmployees) {
      return {
        allowed: false,
        currentCount: 0,
        limit: 0,
        remaining: 0,
        upgradeRequired: 'pro',
        messageAr: 'إدارة الموظفين غير متاحة في خطتك الحالية. قم بالترقية إلى Pro.',
        messageEn: 'Employee management not available in your plan. Upgrade to Premium.',
      };
    }

    const limit = plan.limits.maxEmployees;

    // جلب عدد الموظفين الحالي
    const employeesQuery = query(
      collection(db, 'employees'),
      where('createdBy', '==', teacherId),
      where('isActive', '==', true)
    );
    const snapshot = await getDocs(employeesQuery);
    const currentCount = snapshot.size;

    if (limit === -1) {
      return {
        allowed: true,
        currentCount,
        limit: -1,
        remaining: -1,
        upgradeRequired: null,
        messageAr: 'موظفين غير محدودين',
        messageEn: 'Unlimited employees',
      };
    }

    const allowed = currentCount < limit;
    const remaining = Math.max(0, limit - currentCount);

    return {
      allowed,
      currentCount,
      limit,
      remaining,
      upgradeRequired: allowed ? null : this.nextPlan(sub.planId),
      messageAr: allowed
        ? `يمكنك إضافة ${remaining} موظف إضافي`
        : `وصلت للحد الأقصى (${limit} موظف). قم بالترقية لإضافة المزيد.`,
      messageEn: allowed
        ? `You can add ${remaining} more employees`
        : `Reached limit (${limit} employees). Upgrade to add more.`,
    };
  }

  /** التحقق من إمكانية إنشاء جلسة جديدة */
  async canCreateSession(teacherId: string): Promise<LimitCheckResult> {
    const sub = await this.getOrCreateSubscription(teacherId);
    const plan = PLAN_ENTITLEMENTS[sub.planId];
    const limit = plan.limits.maxSessionsPerMonth;
    const currentCount = sub.usageThisMonth?.sessions || 0;

    if (limit === -1) {
      return {
        allowed: true,
        currentCount,
        limit: -1,
        remaining: -1,
        upgradeRequired: null,
        messageAr: 'جلسات غير محدودة',
        messageEn: 'Unlimited sessions',
      };
    }

    const allowed = currentCount < limit;
    const remaining = Math.max(0, limit - currentCount);

    return {
      allowed,
      currentCount,
      limit,
      remaining,
      upgradeRequired: allowed ? null : this.nextPlan(sub.planId),
      messageAr: allowed
        ? `يمكنك إنشاء ${remaining} جلسة إضافية هذا الشهر`
        : `وصلت للحد الأقصى (${limit} جلسة/شهر). قم بالترقية لإضافة المزيد.`,
      messageEn: allowed
        ? `You can create ${remaining} more sessions this month`
        : `Reached limit (${limit} sessions/month). Upgrade to add more.`,
    };
  }

  /** التحقق من إمكانية استخدام ميزة معينة */
  async canUseFeature(
    teacherId: string,
    feature: 'zoom' | 'coupons' | 'employees' | 'reports'
  ): Promise<{ allowed: boolean; upgradeRequired: SubscriptionPlanId | null; messageAr: string }> {
    const sub = await this.getOrCreateSubscription(teacherId);
    const plan = PLAN_ENTITLEMENTS[sub.planId];

    const featureMap: Record<string, { check: boolean; nameAr: string }> = {
      zoom: { check: plan.limits.canUseZoom, nameAr: 'تكامل Zoom' },
      coupons: { check: plan.limits.canUseCoupons, nameAr: 'نظام الكوبونات' },
      employees: { check: plan.limits.canManageEmployees, nameAr: 'إدارة الموظفين' },
      reports: { check: plan.limits.canExportReports, nameAr: 'تصدير التقارير' },
    };

    const featureInfo = featureMap[feature];
    if (!featureInfo) {
      return { allowed: false, upgradeRequired: null, messageAr: 'ميزة غير معروفة' };
    }

    if (featureInfo.check) {
      return { allowed: true, upgradeRequired: null, messageAr: `${featureInfo.nameAr} متاح` };
    }

    return {
      allowed: false,
      upgradeRequired: this.nextPlan(sub.planId),
      messageAr: `${featureInfo.nameAr} غير متاح في خطتك الحالية. قم بالترقية.`,
    };
  }

  /** جلب إحصائيات الاستخدام الكاملة */
  async getUsageStats(teacherId: string): Promise<UsageStats> {
    const sub = await this.getOrCreateSubscription(teacherId);
    const plan = PLAN_ENTITLEMENTS[sub.planId];

    // جلب عدد الطلاب
    const students = await studentsService.getByTeacherId(teacherId);

    // جلب عدد الفصول
    const classes = await classesService.getByTeacherId(teacherId);

    // جلب عدد الموظفين
    const employeesQuery = query(
      collection(db, 'employees'),
      where('createdBy', '==', teacherId),
      where('isActive', '==', true)
    );
    const employeesSnap = await getDocs(employeesQuery);

    const calcPercentage = (current: number, limit: number) =>
      limit === -1 ? 0 : Math.round((current / limit) * 100);

    return {
      students: {
        current: students.length,
        limit: plan.limits.maxStudents,
        percentage: calcPercentage(students.length, plan.limits.maxStudents),
      },
      classes: {
        current: classes.length,
        limit: plan.limits.maxClasses,
        percentage: calcPercentage(classes.length, plan.limits.maxClasses),
      },
      employees: {
        current: employeesSnap.size,
        limit: plan.limits.maxEmployees,
        percentage: calcPercentage(employeesSnap.size, plan.limits.maxEmployees),
      },
      sessions: {
        current: sub.usageThisMonth?.sessions || 0,
        limit: plan.limits.maxSessionsPerMonth,
        percentage: calcPercentage(
          sub.usageThisMonth?.sessions || 0,
          plan.limits.maxSessionsPerMonth
        ),
      },
    };
  }

  private nextPlan(current: SubscriptionPlanId): SubscriptionPlanId {
    const order: SubscriptionPlanId[] = ['trial', 'solo', 'pro', 'center', 'enterprise'];
    const idx = order.indexOf(current);
    return order[Math.min(idx + 1, order.length - 1)];
  }
}

export const subscriptionService = new SubscriptionService();
export default subscriptionService;
